import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useOrder } from '../../contexts/OrderContext';
import KitchenOrderCard from './KitchenOrderCard';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiRefreshCw } = FiIcons;

const KitchenDisplayFull = () => {
  const { orders, updateOrderStatus, loadOrders, isLoading } = useOrder();
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [previousOrderIds, setPreviousOrderIds] = useState(new Set());
  const [kitchenSettings, setKitchenSettings] = useState({
    normalTime: 15,
    warningTime: 20,
    overdueTime: 25,
    refreshInterval: 30,
  });

  const isInitialMount = useRef(true);

  useEffect(() => {
    const savedSettings = localStorage.getItem('kitchenDisplaySettings');
    if (savedSettings) {
      setKitchenSettings(JSON.parse(savedSettings));
    }
    const savedSoundEnabled = localStorage.getItem('kitchenSoundEnabled');
    if (savedSoundEnabled !== null) {
      setSoundEnabled(JSON.parse(savedSoundEnabled));
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      loadOrders();
    }, Math.max(kitchenSettings.refreshInterval * 1000, 5000));
    return () => clearInterval(interval);
  }, [kitchenSettings.refreshInterval, loadOrders]);

  useEffect(() => {
    const currentOrderIds = new Set(orders.map(order => order.id));

    if (isInitialMount.current) {
      setPreviousOrderIds(currentOrderIds);
      isInitialMount.current = false;
    } else {
      const newOrders = orders.filter(
        order => order.status === 'pending' && !previousOrderIds.has(order.id)
      );
      if (soundEnabled && newOrders.length > 0) {
        playNewOrderSound();
      }
      setPreviousOrderIds(currentOrderIds);
    }
  }, [orders, soundEnabled]);

  const playNewOrderSound = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const playBeep = (frequency, duration, delay = 0) => {
      setTimeout(() => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration);
      }, delay);
    };
    playBeep(800, 0.2, 0);
    playBeep(600, 0.2, 300);
    playBeep(800, 0.3, 600);
  };
  
  const getCustomOrderPriority = (order) => {
    const now = new Date();
    const orderTime = new Date(order.createdAt || order.created_at);
    const minutesElapsed = Math.floor((now - orderTime) / (1000 * 60));
    if (minutesElapsed >= kitchenSettings.overdueTime) return 'overdue';
    if (minutesElapsed >= kitchenSettings.warningTime) return 'urgent';
    return 'normal';
  };

  const activeOrders = orders.filter(o => o.status === 'pending' || o.status === 'preparing');

  return (
    <div className="min-h-screen bg-gray-900 text-white font-sans p-2">
      <main>
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-screen">
            <SafeIcon icon={FiRefreshCw} className="text-8xl text-gray-700 mb-4 animate-spin" />
            <p className="text-gray-500 text-xl">Loading orders...</p>
          </div>
        ) : activeOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-screen">
            <SafeIcon icon={FiCheck} className="text-8xl text-gray-700 mb-4" />
            <p className="text-gray-500 text-xl">All caught up!</p>
            <p className="text-gray-600 text-sm mt-2">No active orders</p>
          </div>
        ) : (
          <motion.div
            key={`active-${activeOrders.length}`}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-6 gap-2"
          >
            {activeOrders
              .sort((a, b) => new Date(a.createdAt || a.created_at) - new Date(b.createdAt || b.created_at))
              .map((order) => (
                <motion.div
                  key={order.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2, layout: { duration: 0.3 } }}
                >
                  <KitchenOrderCard
                    order={order}
                    onStatusChange={updateOrderStatus}
                    priority={getCustomOrderPriority(order)}
                    settings={kitchenSettings}
                  />
                </motion.div>
              ))}
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default KitchenDisplayFull;